import { FetchResult } from '@apollo/client/link/core'
import {
  Form,
  useForm
} from '@cedarjs/forms'
import { toast } from "@cedarjs/web/toast"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Alert, Button, Paper, Typography } from '@mui/material'
import { t } from "i18next"
import { useEffect } from "react"
import { useAuth } from 'src/auth'
import { ControllerDate, ControllerSelect, ControllerTextArea } from "src/components/Controllers/Controllers"
import { getReviewConfig } from "src/const/Defaults"
import { useResourcePermissions } from "src/lib/roles"
import { useDateStore } from "src/lib/store"
import useResetFormWithWorkingDayChange from "src/lib/useResetForm"
import type { FindNewReviewQuery, TankInfosQuery } from 'types/graphql'
import ReviewComment from "../ReviewComment/ReviewComment"
import { ReviewCommentContent } from "../ReviewCard/ReviewCard"

interface ReviewFormProps {
  tank: TankInfosQuery["tank"]
  review: FindNewReviewQuery["review"]
  onSave: (data, id?) => Promise<FetchResult>
  className?: String
}

const ReviewForm = (props: ReviewFormProps) => {
  //Retrieve the current selected date
  const [currentISODate, date] = useDateStore(state => [state.getISODate(), state.date])

  // Create a function to generate form values to avoid duplication
  const getFormValues = () => ({
    idTank: props.tank?.id,
    idBatch: props?.tank?.lastBatchTransfer?.batch.id,
    idWater: props.tank?.idWater,
    description: props.review?.description,
    state: props.review?.state || "",
    date: props.review?.date || currentISODate,
  })

  const { control, handleSubmit, setValue, watch, reset, setError, formState: { errors, isSubmitted, isSubmitting, isValid, isSubmitSuccessful }, register } = useForm({
    defaultValues: getFormValues()
  });
  //To retrieve the sub
  const { currentUser, hasRole } = useAuth()
  
  //Reset form on tank or date change
  useResetFormWithWorkingDayChange(getFormValues, reset, [props.tank?.id])


  // Use the new resource permission system
  const { canInput: canInputReview } = useResourcePermissions({
    hasRole,
    currentUser,
    resource: 'Review',
    data: props?.review,
  })
  const onSubmit = async (data) => {
    if(data.state == "")
      delete data.state
    const result = await props.onSave(data, props.review?.id)
    if (!result.errors) {
      toast.success(`${t('labels.review')} ${t('status.saved')}`)

      return true
    }
    else {
      //@ts-expect-error TODO: Can't find the right type of the result
      setError("root", { type: "server", message: result.errors.message })
      return false
    }
  }

  return (
    <Paper elevation={2} className='mb-4 p-5'>
      <Typography variant='h2' className="pb-6">{`${props.review?.id ? t('actions.edit', { context: "f" }) :t('status.new', { context: "f" })} ${t('labels.review').toLowerCase()}`}
        {props.tank?.name ? <span className="font-normal"> {props.tank.name}</span> : ""}
        {props.tank?.lastBatchTransfer?.batch?.name ? <span className="text-slate-400 font-normal"> - {props.tank.lastBatchTransfer?.batch?.name}</span> : ""}
      </Typography>
      {!canInputReview && <Alert severity="warning" className="mb-4">{t("feedback.notEnoughRightForInput")}</Alert>}
      <Form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col space-y-4 ${!canInputReview ? 'opacity-50 pointer-events-none' : ''}`} aria-readonly={!canInputReview}>
        <div className="flex gap-2 ">

          <ControllerDate
            name="date"
            required
            className="grow w-full"
            error={errors.date}
            control={control}
            // readOnly={!!props?.batch?.id}
            shouldChangeGlobalDate={true}
          />

          <ControllerSelect
            name="state"
            className="grow w-full"
            error={errors.state}
            control={control}
            data={Object.values(getReviewConfig())}
            menuItemRender={(state) => (
              <div className="flex gap-2 items-center justify-between w-full">
                <span>{state.name}</span>
                <FontAwesomeIcon icon={state.icon} className={state.color} />
              </div>
            )}
          />
        </div>
        <ControllerTextArea
          name="description"
          error={errors.description}
          control={control}
          required
        />

        {/* {isSubmitted && !isValid && !isSubmitSuccessful && <Alert severity="error" className="whitespace-pre-line">{Object.values(errors).map((field, index) => <div key={index}>{field.message}</div>)}</Alert>} */}
        {/* {isSubmitSuccessful && <Alert severity="success">{`${t('labels.review')} ${t('status.saved')}. `}</Alert>} */}
        <Button type="submit" className="" variant="contained" disabled={isSubmitting}>{(props.review?.id ? t('actions.edit') : t('status.new_f', { context: "m" })) + ' ' + t('labels.review')}</Button>
      </Form>
      <div className="my-4">
        {props.review?.comments.map(comment =>
          <ReviewCommentContent reviewComment={comment}  key={comment.id} />
        )}
      </div>
      {props.review && <ReviewComment parent={props.review} isInline={true} className=""/> }
    </Paper>
  )
}

export default ReviewForm
